/**
 * CryptoUtils - Encryption utilities for WalletVault
 * Uses Web Crypto API for AES-256-GCM encryption
 */

// Generate random salt
export function generateSalt() {
  return crypto.getRandomValues(new Uint8Array(16));
}

// Derive key from password using PBKDF2
export async function deriveKey(password, salt) {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// Encrypt data with AES-256-GCM
export async function encrypt(data, key) {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(JSON.stringify(data))
  );

  // Combine IV + encrypted data
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  
  return btoa(String.fromCharCode(...combined));
}

// Decrypt data with AES-256-GCM
export async function decrypt(encryptedData, key) {
  const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
  const iv = combined.slice(0, 12);
  const data = combined.slice(12);

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    data
  );

  const decoder = new TextDecoder();
  return JSON.parse(decoder.decode(decrypted));
}

// Fixed salt for signature-based encryption (deterministic per-wallet)
const SIGNATURE_SALT = new Uint8Array([
  0x59, 0x6f, 0x42, 0x69, 0x74, 0x63, 0x68, 0x56,
  0x61, 0x75, 0x6c, 0x74, 0x53, 0x61, 0x6c, 0x74
]); // "YoBitchVaultSalt"

/**
 * Derive encryption key from wallet signature
 * Used for wallet-based authentication (no password needed)
 * @param {Uint8Array} signatureBytes - The signature bytes from wallet.signMessage()
 * @returns {Promise<CryptoKey>} AES-256-GCM key
 */
export async function deriveKeyFromSignature(signatureBytes) {
  // Hash the signature to get consistent key material
  const signatureHash = await crypto.subtle.digest('SHA-256', signatureBytes);
  
  // Import the hash as key material for PBKDF2
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    signatureHash,
    'PBKDF2',
    false,
    ['deriveKey']
  );
  
  // Derive the final AES key using PBKDF2
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: SIGNATURE_SALT,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Generate the challenge message for wallet signing
 * @param {string} publicKey - The wallet's public key
 * @returns {string} The message to sign
 */
export function getUnlockChallengeMessage(publicKey) {
  return `Unlock YoBitch Wallet Vault\n\nWallet: ${publicKey}\n\nThis signature will be used to encrypt your local data. Only you can decrypt it.`;
}

export default {
  generateSalt,
  deriveKey,
  deriveKeyFromSignature,
  getUnlockChallengeMessage,
  encrypt,
  decrypt
};
